import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, User, Tag } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTASection from '../components/common/CTASection';
import { BreadcrumbSchema } from '../components/seo/SchemaMarkup';
import Seo from '../components/seo/Seo';
import { blogPosts } from '../data/mock';
import { seoConfig } from '../data/seoConfig';

const BlogPage = () => {
  const breadcrumbs = [
    { name: 'Home', url: 'https://basinplumbing.com/' },
    { name: 'Blog', url: 'https://basinplumbing.com/blog/' }
  ];

  return (
    <>
      <Seo {...seoConfig.blog} />
      <BreadcrumbSchema items={breadcrumbs} />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#1a3a5c] to-[#0f2438]">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="text-sm text-blue-200 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-white">Blog</span>
            </nav>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Plumbing Tips & Insights
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl">
              Expert advice, how-to guides, and the latest plumbing news from the 
              Basin Plumbing team.
            </p>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`}>
                  <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 group overflow-hidden">
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(post.date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {post.readTime}
                        </span>
                      </div>
                      
                      <h2 className="text-xl font-semibold text-[#1a3a5c] mb-3 group-hover:text-[#e85a24] transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>

                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 text-sm text-gray-500">
                          <Tag size={14} />
                          {post.category}
                        </span>
                        <span className="text-[#e85a24] font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                          Read More
                          <ArrowRight size={16} />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection />
      </main>

      <Footer />
    </>
  );
};

export default BlogPage;
